import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { parseQueryString } from "@client/lib/api";
import { type BaseAppState, type EntityRef, Status } from "@client/types";
import toasts from "@common/lib/toast";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type MemberSurvey } from "./types";

export enum Actions {
  InitBegin = "MEMBER_SURVEY_INIT_BEGIN",
  InitFailure = "MEMBER_SURVEY_INIT_FAILURE",
  InitSuccess = "MEMBER_SURVEY_INIT_SUCCESS",

  CreateUserFeedbackBegin = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_BEGIN",
  CreateUserFeedbackFailure = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_FAILURE",
  CreateUserFeedbackSuccess = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_SUCCESS",

  UpdateUserFeedbackBegin = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_BEGIN",
  UpdateUserFeedbackFailure = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_FAILURE",
  UpdateUserFeedbackSuccess = "MEMBER_SURVEY_CREATE_USER_FEEDBACK_SUCCESS"
}

export type WithItemId = {
  ticketId: string;
};
export type InitBeginActionData = EntityRef & WithItemId;
export interface InitSuccessActionData {
  questions: MemberSurvey.TicketFeedbackQuestion[];
  answers: MemberSurvey.UserTicketFeedback[];
}
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  MemberSurvey.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.init(apiInfo, { ticketId: params.ticketId });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  const ticketId = parseQueryString().t ?? parseQueryString().ticketid;
  if (ticketId) {
    dispatch(_init({ ...args, ticketId: ticketId }));
  } else if (localState.status === Status.None) {
    dispatch({
      type: Actions.InitSuccess,
      data: <InitSuccessActionData>{
        id: args.id,
        answers: [],
        questions: []
      }
    });
  }
};

export type CreateAnswerBeginActionData = EntityRef &
  WithItemId & {
    answer: string | null;
    rating: number | null;
    ticketFeedbackQuestionId: string;
  };

export type CreateAnswerSuccessActionData = { userTicketFeedbacks: MemberSurvey.Api.UserTicketFeedback[] };

export const _createAnswer = apiAction<
  BaseAppState,
  CreateAnswerBeginActionData,
  MemberSurvey.Api.CreateUserFeedbackResponse,
  CreateAnswerSuccessActionData
>({
  types: {
    begin: Actions.CreateUserFeedbackBegin,
    failure: Actions.CreateUserFeedbackFailure,
    success: [
      Actions.CreateUserFeedbackSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CreateAnswerBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.createAnswer(apiInfo, {
      ...params
    });
  }
});

export const createAnswer =
  (ref: EntityRef, rating: number | null, ticketFeedbackQuestionId: string, answer: string | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);
    const ticketId = parseQueryString().t ?? parseQueryString().ticketid;

    if (localState.status !== "Creating") {
      dispatch(_createAnswer({ ...ref, ticketId: ticketId, rating, ticketFeedbackQuestionId, answer }));
    }
  };

export type UpdateAnswerBeginActionData = CreateAnswerBeginActionData;

export type UpdateAnswerSuccessActionData = CreateAnswerSuccessActionData;

export const _updateAnswer = apiAction<
  BaseAppState,
  UpdateAnswerBeginActionData,
  MemberSurvey.Api.UpdateUserFeedbackResponse,
  UpdateAnswerSuccessActionData
>({
  types: {
    begin: Actions.CreateUserFeedbackBegin,
    failure: Actions.CreateUserFeedbackFailure,
    success: [
      Actions.CreateUserFeedbackSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: UpdateAnswerBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.updateAnswer(apiInfo, {
      ...params
    });
  }
});

export const updateAnswer =
  (ref: EntityRef, rating: number | null, ticketFeedbackQuestionId: string, answer: string | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);
    const ticketId = parseQueryString().t ?? parseQueryString().ticketid;

    if (localState.status !== "Updating") {
      dispatch(_updateAnswer({ ...ref, ticketId: ticketId, rating, ticketFeedbackQuestionId, answer }));
    }
  };
